import { Button } from 'components/ui/button';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Input } from 'components/ui/input';
import { BadgeAlert, LoaderIcon } from 'lucide-react';
import serviceLimitApis from 'features/bot-builder/services/serviceLimitApi';
import { useState } from 'react';
import { serviceMappings } from 'features/bot-builder/constants';

const ServiceLimit = ({
  open,
  setOpen,
  serviceId,
  serviceName,
  botId,
}: {
  open: boolean;
  setOpen: (value: boolean) => void;
  serviceId: number;
  serviceName: string;
  botId: string;
}) => {
  let service;
  try {
    // Add robust null checking
    if (serviceName && typeof serviceName === 'string' && serviceMappings && serviceMappings[serviceName]) {
      service = serviceMappings[serviceName];
    } else {
      // Fallback if service isn't found
      service = { label: serviceName || 'Service', icon: null };
    }
  } catch (error) {
    console.error("Error accessing service mapping:", error);
    service = { label: 'Service', icon: null };
  }
  
  const headings = ['Usage limit', 'Reset period'];
  const [input, setInput] = useState<{
    usage_limit: number;
    reset_period: 'DAILY' | 'MONTHLY' | 'YEARLY';
  }>({
    usage_limit: 1,
    reset_period: 'DAILY',
  });
  console.log(input);
  const [isAdded, setIsAdded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const createLimit = async (id: number) => {
    setLoading(true);
    const serviceInput = {
      telegram_bot_id: botId,
      service_id: id,
      usage_limit: input?.usage_limit || 1,
      reset_period: input?.reset_period || 'DAILY',
    };

    try {
      const response = await serviceLimitApis.createLimit(serviceInput);
      if (response.success) {
        setIsAdded(true);
        setOpen(false);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Overlay className="fixed inset-0 bg-black/50" />
      <Dialog.Content className="absolute z-10 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-lg shadow-lg p-4 sm:p-9 flex flex-col gap-4 sm:gap-6 bg-[#EEF1F0] w-[90%] sm:w-[560px] max-w-[560px]">
        <Dialog.Title className="text-xl sm:text-2xl text-left font-bold text-[#1A211E]">
          {service.label}
        </Dialog.Title>

        <div className="space-y-3 w-full">
          {/* headings */}
          <h1 className="font-bold text-[13px] sm:text-[14px]">
            Advanced configuration
          </h1>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 sm:gap-y-0 sm:gap-x-3">
            {headings.map((heading, idx) => (
              <div key={idx} className="flex items-center gap-1">
                <span className="text-[13px] sm:text-[14px] font-normal text-[#1C2024]">
                  {heading}
                </span>
                <BadgeAlert size={15} className="cursor-pointer" />
              </div>
            ))}
          </div>
          {/* inputs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 sm:gap-y-0 sm:gap-x-3">
            <Input
              onChange={(e) =>
                setInput((prev) => ({
                  ...prev,
                  usage_limit: Number(e.target.value),
                }))
              }
              className="border-2 focus:outline-none focus:ring-0 placeholder:text-[#B8BCBA] placeholder:font-normal placeholder:text-[14px] sm:placeholder:text-[16px] bg-[#FBFDFC] focus:border-[#846CEC]"
            />
            <Select
              defaultValue={input.reset_period}
              onValueChange={(value) =>
                setInput((prev) => ({
                  ...prev,
                  reset_period: value as 'DAILY' | 'MONTHLY' | 'YEARLY',
                }))
              }
            >
              <SelectTrigger
                className={`w-full bg-[#FBFDFC] border-2 rounded-md text-[14px] sm:text-[16px]`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="DAILY">Daily</SelectItem>
                <SelectItem value="MONTHLY">Monthly</SelectItem>
                <SelectItem value="YEARLY">Yearly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-end w-full gap-2 sm:gap-3">
          <Button
            variant="customSecondary"
            size="sm"
            className="text-[13px] sm:text-[14px] font-normal w-full sm:w-auto"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>

          <Button
            variant="customPrimary"
            size="sm"
            className="text-[13px] sm:text-[14px] font-normal w-full sm:w-auto"
            type="button"
            onClick={() => createLimit(serviceId)}
            disabled={loading}
          >
            {loading && (
              <div className="animate-spin">
                <LoaderIcon size={20} />
              </div>
            )}
            {isAdded ? <span>Saved</span> : <span>Save</span>}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default ServiceLimit;
